import {TiberFormHtmlForm} from "./tiber-form-html-form.js";
import {TiberFormRestForm} from "./tiber-form-rest-form.js";

/**
 * The `FormLoader` class is responsible for loading and initializing forms based on given configuration.
 * It provides functionality to fetch a JSON configuration file and setup forms dynamically based on
 * specific form types and definitions.
 */
export class FormLoader {
    /**
     * A mapping that associates form types with their corresponding form handler classes.
     * Keys represent the type of form (e.g., "form", "json"), and values represent
     * the class or object responsible for handling that specific form type.
     *
     * The map includes:
     * - HTML form types such as "form" and "wwwencoded" that are associated with TiberFormHtmlForm.
     * - REST API-related form types such as "json", "rest", and "api" that are associated with TiberFormRestForm.
     *
     * This mapping is used to dynamically determine the appropriate handler for a given form type.
     *
     * @type {Object.<string, Function>}
     */
    static formTypeMap = {
        "form": TiberFormHtmlForm,
        "wwwencoded": TiberFormHtmlForm,

        "json": TiberFormRestForm,
        "rest": TiberFormRestForm,
        "api": TiberFormRestForm
    }

    /**
     * Loads the configuration
     * @param jsonPath The path to the json configuration
     * @returns {Promise<any|null>} an awaitable promise containing the definition file.
     */
    static async loadJson(jsonPath) {
        try {
            const response = await fetch(jsonPath);
            if (!response.ok) {
                throw new Error(`Unable to load configuration from ${jsonPath}`);
            }

            return await response.json();
        } catch (error) {
            console.error(error.message);
            return null;
        }
    }

    /**
     * Load the JSON configuration to be used with the forms builder
     * @param jsonPath Path to the
     * @returns {Promise<any|null>}
     */
    static async loadForms(forms) {
        for (const element of forms) {
            let formId = element.dataset.formId;
            const configJson = element.dataset.config ?? element.dataset.configUrl ?? "/assets/forms/tiber.json";
            const formsConfig = await FormLoader.loadJson(configJson);
            const token = element.dataset.token;

            const phpRoot = FormLoader.findPhpRoot(configJson);

            if (formsConfig === null) {
                continue;
            }

            let configuration = {};
            if (formsConfig.forms !== undefined) {
                if ((formId == null || formId.length === 0) && formsConfig.forms.length === 1 ) {
                    formId = formsConfig.forms[0].id;
                    element.dataset.formId = formId;

                    configuration = formsConfig.forms[0];
                } else {
                    configuration = formsConfig.forms.find(form => form.id === formId);
                }
            }

            if (configuration === undefined || configuration === null) {
                console.error(`Unable to find form definition for id ${formId}`);
                continue;
            }

            if ((token ?? "").length > 0) {
                configuration.token = token;
            }

            if (formId) {
                const formMethod = (configuration.callback?.type ?? "form").toLowerCase();
                const formClass = FormLoader.formTypeMap[formMethod];

                if (formClass == null) {
                    throw new Error(`Form method not defined: ${formMethod}`);
                }

                await new formClass(element, configuration).initialize(phpRoot);
            } else {
                console.error('Unable to load form missing form id. - skipping / no loading');
            }
        }
    }

    static findPhpRoot(path) {
        if (!path || !path.toLowerCase().includes('.php')) {
            return "/";
        }
        const parts = path.split('/').filter(Boolean);
        const rootPathParts = [];
        for (let part of parts) {
            if (part.toLowerCase().includes('.php')) {
                break;
            }
            rootPathParts.push(part);
        }

        let rootPath =  rootPathParts.map(item => /^https?/.test(item.toLowerCase()) ? item + "/" : item).join('/');
        if (!rootPath.startsWith('/') && !/^https?:\/\//.test(path)) {
            rootPath = '/' + rootPath;
        }

        return rootPath;
    }

}